import type { SearchArticleMeta } from "@serophin/nottldr-shared";
import { MoreVertical } from "lucide-preact";
import { useState } from "preact/hooks";
import { absoluteUrl } from "../../siteMeta";
import { ShareModal } from "../share/ShareModal";
import { ArticleMenuItems } from "./ArticleMenuItems";
import { CollectionsModal } from "./CollectionsModal";
import { Dropdown } from "./Dropdown";
import { ReportArticleModal } from "./ReportArticleModal";

type ArticleActionsMenuProps = {
  meta: SearchArticleMeta;
  class?: string;
  iconClass?: string;
  iconSize?: number;
  backgroundClass?: string;
};

type Modal = "collections" | "share" | "report";

/**
 * The three-dots menu of an article: read later, collections, mark read, follow, share, report. What it shows
 * about the visitor is read only once it is opened, so a list of cards does not read the library for every card.
 */
export const ArticleActionsMenu = ({ meta, class: className = "", iconClass = "", iconSize = 20, backgroundClass = "" }: ArticleActionsMenuProps) => {
  const [modal, setModal] = useState<Modal | null>(null);
  const close = () => setModal(null);

  return (
    <>
      <Dropdown
        align="right"
        trigger={
          <button
            type="button"
            class={`p-2 rounded-full transition-colors duration-200 ${backgroundClass || "hover:bg-gray-100/10 dark:hover:bg-gray-700/20"} ${className}`}
            aria-label="Article actions"
            data-test-click="article-menu"
            data-test-article={meta.id}
            data-test-author={meta.authorAlias}
          >
            <MoreVertical size={iconSize} class={iconClass} />
          </button>
        }
      >
        {(closeMenu) => <ArticleMenuItems meta={meta} closeMenu={closeMenu} openModal={setModal} />}
      </Dropdown>
      {modal === "collections" && <CollectionsModal authorAlias={meta.authorAlias} articleId={meta.id} isOpen onClose={close} />}
      <ShareModal
        isOpen={modal === "share"}
        onClose={close}
        url={absoluteUrl(meta.path)}
        title={meta.title}
        text={meta.tagline}
        articleId={meta.id}
        authorAlias={meta.authorAlias}
      />
      <ReportArticleModal isOpen={modal === "report"} onClose={close} articleId={meta.id} authorAlias={meta.authorAlias} />
    </>
  );
};
